import * as XLSX from "xlsx";
import { notify } from "./toast";
import { QCStatus } from "../enum/common";

interface QCRequestPartNumber {
  qcStatus: QCStatus;
  id: number;
  partNumber: string;
  description: string;
  manufacturer: string;
  actualQuantity: number;
  unit: string;
  project: string;
  projectCode: string;
  importDate: Date;
  location: {
    id: number;
    code: string;
    warehouse: {
      warehouseCode: string;
    };
  };
  purchaser: string;
  supplierType: string;
}

const REQUIRED_HEADERS = [
  "Ngày hàng về/\r\nIncoming date",
  "Dự án/\r\nProject",
  "Mã dự án/\r\nProject code",
  "Mã sản xuất/\r\nMPN",
  "Nhà sản xuất/MFR",
  "Mô tả ",
  "Đơn vị/\r\nUnit",
  "SL thực tế/\r\nActual Q'ty",
  "AWB",
  "Loại NCC/\r\nType",
  "Người mua/\r\nPur",
  "Loại vật liệu/\r\nMaterials",
  "Nhà cung cấp/\r\nSupplier",
  "Khách hàng/\r\nCustomer",
  "Số lượng kiểm tra/\r\nSampling size",
  "Số lượng lỗi/\r\nQ'ty NG",
  "Kết luận/ Result",
  "Date code",
  "Đóng gói/\r\nPacking",
  "Xuất Xứ/\r\n",
  "Độ nhạy ẩm/\r\nMSL",
  "Lỗi Thật/Ảo",
  "Mô tả chi tiết/\r\nDetail description",
  "Hành động/\r\nAction",
  "Theo dõi/\r\nFollow",
  "Loại lỗi/\r\nDefect",
  "Ghi chú ",
];

/**
 * Generate Excel template for bulk QC evaluation
 * @param requestNumber - QC Request number
 * @param requestData - QC Request data (AWB, import date)
 * @param partNumbers - List of part numbers in the request
 */
export const generateQCEvaluationTemplate = (
  requestNumber: string,
  awb: string,
  partNumbers: QCRequestPartNumber[],
): void => {
  const excelData = partNumbers
    .filter((item) => item.qcStatus === QCStatus.PROCESSING)
    .map((item) => {
      const row: any = {};
      const incomingDate = item.importDate
        ? new Date(item.importDate).toLocaleDateString("vi-VN")
        : "";

      row["Ngày hàng về/\r\nIncoming date"] = incomingDate;
      row["Dự án/\r\nProject"] = item.project || "";
      row["Mã dự án/\r\nProject code"] = item.projectCode || "";
      row["Mã sản xuất/\r\nMPN"] = item.partNumber;
      row["Nhà sản xuất/MFR"] = item.manufacturer || "";
      row["Mô tả "] = item.description || "";
      row["Đơn vị/\r\nUnit"] = item.unit || "";
      row["SL thực tế/\r\nActual Q'ty"] = item.actualQuantity;
      row["AWB"] = awb;
      row["Loại NCC/\r\nType"] = item.supplierType;
      row["Người mua/\r\nPur"] = item.purchaser;
      row["Loại vật liệu/\r\nMaterials"] = "";
      row["Nhà cung cấp/\r\nSupplier"] = "";
      row["Khách hàng/\r\nCustomer"] = "";
      row["Số lượng kiểm tra/\r\nSampling size"] = "";
      row["Số lượng lỗi/\r\nQ'ty NG"] = "";
      row["Kết luận/ Result"] = "";
      row["Date code"] = "";
      row["Đóng gói/\r\nPacking"] = "";
      row["Xuất Xứ/\r\n"] = "";
      row["Độ nhạy ẩm/\r\nMSL"] = "";
      row["Lỗi Thật/Ảo"] = "";
      row["Mô tả chi tiết/\r\nDetail description"] = "";
      row["Hành động/\r\nAction"] = "";
      row["Theo dõi/\r\nFollow"] = "";
      row["Loại lỗi/\r\nDefect"] = "";
      row["Ghi chú "] = "";
      return row;
    });

  const ws = XLSX.utils.json_to_sheet(excelData, { header: REQUIRED_HEADERS });

  const colWidths = [
    { wch: 15 }, // Ngày hàng về
    { wch: 20 }, // Dự án
    { wch: 15 }, // Mã dự án
    { wch: 20 }, // MPN
    { wch: 20 }, // Nhà sản xuất
    { wch: 40 }, // Mô tả
    { wch: 10 }, // Đơn vị
    { wch: 12 }, // SL thực tế
    { wch: 15 }, // AWB
    { wch: 12 }, // Loại NCC
    { wch: 12 }, // Người mua
    { wch: 15 }, // Loại vật liệu
    { wch: 20 }, // Nhà cung cấp
    { wch: 15 }, // Khách hàng
    { wch: 15 }, // Sampling size
    { wch: 12 }, // Q'ty NG
    { wch: 15 }, // Result
    { wch: 12 }, // Date code
    { wch: 12 }, // Packing
    { wch: 12 }, // Xuất xứ
    { wch: 10 }, // MSL
    { wch: 15 }, // Inspection rate
    { wch: 12 }, // Lỗi Thật/Ảo
    { wch: 40 }, // Detail description
    { wch: 30 }, // Action
    { wch: 30 }, // Follow
    { wch: 20 }, // Defect
    { wch: 30 }, // Ghi chú
  ];
  ws["!cols"] = colWidths;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "QC Evaluation");

  const instructions = [
    ["HƯỚNG DẪN ĐIỀN FORM ĐÁNH GIÁ QC HÀNG LOẠT"],
    [""],
    ["CÁC CỘT ĐÃ ĐIỀN SẴN (KHÔNG THAY ĐỔI):"],
    ["• Ngày hàng về, Dự án, Mã dự án, Mã sản xuất (MPN)"],
    ["• Nhà sản xuất, Mô tả, Đơn vị, SL thực tế, AWB"],
    [""],
    ["CÁC CỘT BẮT BUỘC PHẢI ĐIỀN:"],
    ["1. Loại nhà cung cấp"],
    ["2. Người mua: Tên người mua hàng"],
    ["3. Loại vật liệu: Loại vật liệu"],
    ["4. Nhà cung cấp: Tên nhà cung cấp"],
    ["5. Khách hàng: Tên khách hàng"],
    ["6. Số lượng kiểm tra: Số lượng mẫu kiểm tra"],
    ["7. Số lượng lỗi: Số lượng NG phát hiện"],
    ["8. Kết luận: PASS hoặc FAIL"],
    ["9. Date code: Mã date code"],
    ["10. Đóng gói: Thông tin đóng gói"],
    ["11. Xuất xứ: Nơi xuất xứ"],
    ["12. MSL: Độ nhạy ẩm"],
    ["13. Mô tả chi tiết: Mô tả chi tiết kết quả"],

    [""],
    ["CÁC CỘT TỰ ĐỘNG TÍNH (KHÔNG ĐIỀN):"],
    ["• Week, Month: Hệ thống tự tính từ ngày kiểm tra"],
    ["• Tỷ lệ lỗi (NG rate): Tự động = (Q'ty NG / Sampling size) × 100"],
    ["• Tỷ lệ kiểm tra: Tự động = (Sampling size / Actual Q'ty) × 100"],
    ["• Người kiểm tra: Người upload file"],
    [""],

    ["LƯU Ý QUAN TRỌNG:"],
    ["- KHÔNG XÓA hoặc THÊM dòng dữ liệu"],
    ["- KHÔNG THAY ĐỔI các cột đã điền sẵn"],
    [
      "- Server sẽ tự động lấy vị trí (Location) từ hệ thống - Nếu QC Fail sẽ trả về vị trí kho QC1 - Pass sẽ trả về vị trí kho ban đầu",
    ],
    [""],
  ];

  const wsInstructions = XLSX.utils.aoa_to_sheet(instructions);
  wsInstructions["!cols"] = [{ wch: 100 }];
  XLSX.utils.book_append_sheet(wb, wsInstructions, "Hướng dẫn");

  const fileName = `QC_Evaluation_${requestNumber}_${new Date().getTime()}.xlsx`;

  XLSX.writeFile(wb, fileName);

  notify.success(`Đã tải xuống template ${fileName}`);
};
